/**
 * Feeds Page (layoutless)
 *
 * Public product feed landing page for syndication or catalog exports.
 * Links to the externally-hosted Google Merchant XML feed (e.g. DataFeedWatch).
 */

import { useEffect } from 'react';
import styled from '@emotion/styled';
import { env } from '@/lib/utils/env';

const Wrap = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 3rem 1.5rem;
  font-family: system-ui, -apple-system, sans-serif;
  color: #1a1a1a;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0 0 0.5rem;
`;

const Meta = styled.p`
  font-size: 0.875rem;
  color: #666;
  margin: 0 0 2rem;
`;

const Card = styled.div`
  background: #f8f8f8;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;

  h2 {
    font-size: 1.125rem;
    font-weight: 600;
    margin: 0 0 0.75rem;
  }

  ul {
    margin: 0;
    padding-left: 1.25rem;
  }

  li {
    margin-bottom: 0.5rem;
    line-height: 1.5;
  }
`;

const FeedButton = styled.a`
  display: inline-block;
  background: #1a1a1a;
  color: #fff;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 600;
  margin-bottom: 1rem;

  &:hover {
    background: #333;
  }
`;

const Code = styled.code`
  background: #e8e8e8;
  padding: 0.125rem 0.375rem;
  border-radius: 3px;
  font-size: 0.85em;
`;

const Small = styled.p`
  font-size: 0.8rem;
  color: #888;
  margin: 0.5rem 0 0;
`;

export function FeedsPage() {
  const feedUrl = env.PUBLIC_PRODUCT_FEED_URL;
  const baseUrl = env.BASE_URL;

  useEffect(() => {
    document.title = 'Public Product Feed';
  }, []);

  return (
    <Wrap>
      <Title>Public Product Feed</Title>
      <Meta>
        Format: Google Merchant XML &middot; Hosted externally
      </Meta>

      {feedUrl ? (
        <Card>
          <FeedButton href={feedUrl} rel="nofollow noopener" target="_blank">
            View / Download Feed (XML)
          </FeedButton>
          <Small>
            URL: <Code>{feedUrl}</Code>
          </Small>
          <Small>
            If the feed structure changes in future, we will version it (e.g.{' '}
            <Code>/v2/products.xml</Code>) and keep old URLs live for a grace period.
          </Small>
        </Card>
      ) : (
        <Card>
          <p>
            No product feed URL has been configured. Set{' '}
            <Code>VITE_PUBLIC_PRODUCT_FEED_URL</Code> to enable the feed link.
          </p>
        </Card>
      )}

      <Card>
        <h2>For developers &amp; agents</h2>
        <ul>
          <li>
            Encoding: UTF-8, MIME type: <Code>application/xml</Code>
          </li>
          <li>
            Fields follow Google Merchant XML specs (e.g. <Code>g:id</Code>,{' '}
            <Code>g:title</Code>, <Code>g:price</Code>,{' '}
            <Code>g:availability</Code>).
          </li>
          <li>
            Consumers should implement caching with ETag / Last-Modified if available.
          </li>
        </ul>
      </Card>

      {baseUrl && (
        <Small>
          Canonical: <Code>{baseUrl}/feeds</Code>
        </Small>
      )}
    </Wrap>
  );
}
